// Google Analytics Service
// Provides a centralized way to track events, page views, and user interactions

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

interface EventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: any;
}

class GoogleAnalyticsService {
  private measurementId: string;

  constructor(measurementId: string = 'G-XXXXXXXXXX') {
    this.measurementId = measurementId;
  }

  // Initialize Google Analytics (called from App component)
  initialize() {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', this.measurementId);
    }
  }

  // Track page views
  trackPageView(pagePath: string, pageTitle?: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
      });
    }
  }

  // Track custom events
  trackEvent(action: string, params?: EventParams) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', action, {
        event_category: params?.event_category || 'engagement',
        ...params,
      });
    }
  }

  // Track user login
  // trackLogin(method: string = 'email') {
  //   this.trackEvent('login', {
  //     event_category: 'auth',
  //     method,
  //   });
  // }

  // Track user registration
  trackSignUp(method: string = 'email') {
    this.trackEvent('sign_up', {
      event_category: 'auth',
      method,
    });
  }

  // Track search
  trackSearch(searchTerm: string, searchType?: string) {
    this.trackEvent('search', {
      event_category: 'engagement',
      search_term: searchTerm,
      search_type: searchType,
    });
  }

  // Track feature usage
  trackFeatureUse(featureName: string, action?: string) {
    this.trackEvent('feature_use', {
      event_category: 'features',
      event_label: featureName,
      action,
    });
  }

  // Track errors
  trackError(errorMessage: string, errorType?: string) {
    this.trackEvent('exception', {
      event_category: 'errors',
      description: errorMessage,
      fatal: false,
      error_type: errorType,
    });
  }

  // Track conversions
  trackConversion(conversionType: string, value?: number) {
    this.trackEvent('conversion', {
      event_category: 'conversions',
      event_label: conversionType,
      value,
    });
  }

  // Track timing
  trackTiming(category: string, variable: string, value: number, label?: string) {
    this.trackEvent('timing_complete', {
      event_category: category,
      name: variable,
      value,
      event_label: label,
    });
  }

  // Set user properties
  setUserProperties(properties: Record<string, any>) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('set', 'user_properties', properties);
    }
  }

  // Track custom dimensions
  setCustomDimension(dimensionName: string, value: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', this.measurementId, {
        custom_map: { [dimensionName]: value },
      });
    }
  }
}

// Export singleton instance
const googleAnalytics = new GoogleAnalyticsService();
export default googleAnalytics;